<template>
    <ContentComp
        :load-state="HandleIsonenEventLoadState"
        :fillContent="true"
        :show-as-modal="true"
    >
        <FormLayout>
            <FormLayoutRow v-if="false">
                <FormLayoutLabel text="eventinfo.externaldatasource" />

                <DropdownSelect
                    class="flex-fill"
                    caption="eventinfo.selectexternaldatasource"
                    :data="computedExternalDatasources"
                    v-model="selectedSource"
                />
            </FormLayoutRow>
            <FormLayoutRow v-else-if="!selectedSource">
                <span
                    v-for="(source, i) in computedExternalDatasources"
                    :key="i"
                >
                    <ButtonComp
                        button-type="Secondary"
                        textposition="left"
                        class="bg-white opacity-75 opacity-hover-100"
                        @click="selectedSource = source.value"
                        :class="{
                            'opacity-100': selectedSource === source.value,
                        }"
                        :style="{
                            'background-image': `url(${source.color})`,
                            'background-size': '95% 95%',
                            'background-position': 'center',
                            width: '200px',
                        }"
                    >
                        <span>&nbsp;</span>
                    </ButtonComp>
                </span>
            </FormLayoutRow>
        </FormLayout>

        <template v-if="!processedFile">
            <h4 class="mb-5">
                {{ $tm('import.fromisonenapi') }}
            </h4>

            <FormLayout>
                <FormLayoutRow>
                    <FormLayoutLabel text="import.club" />

                    <DropdownSelect
                        class="flex-fill"
                        :show-search="true"
                        :loadstate="GetIsonenItemsLoadState"
                        :data="isonenDropdownElements"
                        v-model="selectedIsonenItem"
                    />
                </FormLayoutRow>
                <FormLayoutRow v-if="availableExternalDatasourceEvents !== null">
                    <FormLayoutLabel text="eventwizard.event" />

                    <DropdownSelect
                        class="flex-fill"
                        :data="isonenEventsDropdownElements"
                        v-model="selectedExternalDatasourceEventKey"
                        caption="externaleventsource.selectevent"
                        :loadstate="FetchIsonenEventLoadState"
                        :show-item-count-badge="true"
                    />
                </FormLayoutRow>
                <FormLayoutRow v-if="externalEventsLoadFailed !== false">
                    <FormLayoutLabel />

                    <AlertMessage
                        class="w-100 flex-fill"
                        innerclass="flex-fill"
                        size="sm"
                        type="danger"
                    >
                        <i18next
                            :translation="$t('externaleventsource.eventsloadfailed')"
                            v-if="externalEventsLoadFailed === 'timeout'"
                        >
                            <template #action>
                                <a
                                    href="#"
                                    @click="fetchEventsForCurrentItem"
                                    >{{ $tm('general.click') }}</a
                                >
                            </template>
                        </i18next>
                        <span
                            v-else
                            v-html="$tm('externaleventsource.eventsloadfailedunauthorized', '', true)"
                        ></span>
                    </AlertMessage>
                </FormLayoutRow>
            </FormLayout>

            <h4 class="mb-5 mt-5">{{ $tm('import.fromisonenfile') }}</h4>

            <ErrorMessage
                :message="errorMessage"
                v-if="errorMessage"
            />
            <div
                class="mt-3"
                v-if="selectedExternalDatasource && selectedExternalDatasource.Type == 'FileDrop'"
            >
                <Dropzone
                    ref="dropzone"
                    class="flex-grow-1 border"
                    :multiple="false"
                    :showFiles="false"
                    @change="onFileChanged"
                />
            </div>
        </template>

        <div v-else>
            <div class="d-flex flex-column gap-3">
                <AlertMessage
                    type="info"
                    v-if="modelList.length > 1"
                    >{{ $tm('externaldatasource.filecontainsmoreevents') }}</AlertMessage
                >
                <GridComp
                    v-if="modelList.length > 1"
                    ref="grid"
                    class="flex-grow-1 mb-5"
                    :gridSetup="gridSetup"
                    @on-cell-clicked="onCellClicked"
                />

                <EventinfoDetailsView
                    v-if="model.EventInfo"
                    v-model="model.EventInfo"
                />

                <DisplaySeparator v-model="separator" />

                <ContestantEventDetailsView
                    v-if="model"
                    :categories="model.Categories"
                    :races="model.Races"
                    :contestantcount="model.Contestants?.length || 0"
                    :categories-group-connection="model.CategoryGroupConnection"
                    :duplicates="duplicates"
                />
            </div>
        </div>
    </ContentComp>
</template>
<script setup lang="ts">
import {
    AlertMessage,
    ButtonComp,
    ContentComp,
    DateComp,
    DisplaySeparator,
    DropdownSelect,
    Dropzone,
    ErrorMessage,
    GridComp,
} from '@/components';
import { FormLayoutRow } from '@/components/Form';
import FormLayout from '@/components/Form/FormLayout.vue';
import FormLayoutLabel from '@/components/Form/FormLayoutLabel.vue';
import { useHelper, useTaskLoader } from '@/composeables';
import { ExternalEventDatasourceDao } from '@/dao';
import type { SelectLine, SelectLineValueType } from '@/objects';
import {
    CreateEventRequest,
    EventinfoBase,
    ExternalDataSource,
    ExternalDatasourceEvent,
    ExternalEventDatasourceResponse,
    ImportDuplicateContestant,
    ISonenItem,
} from '@/objects/generated';
import { useToastStore } from '@/stores';
import { computed, onMounted, ref, watch } from 'vue';
import ContestantEventDetailsView from '../Contestants/ContestantEventDetailsView.vue';
import EventinfoDetailsView from './EventinfoDetailsView.vue';
import { GridColumnDefinition, GridDataSource, GridSetup, type IGridCompExposed } from '@/components/Grid/Classes';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import ValidatedText from '@/components/Basic/ValidatedText.vue';
import ValidatedInt from '@/components/Basic/ValidatedInt.vue';
import type { ITranslationKey } from '@/objects/TranslationKey';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';
import { formatDateDefault } from '@/utilities/dateutils';

/**
 * This will get a list of the logged in users external datasources
 *
 * NOTE! For now it is hardcoded to show isonen.
 *
 * TODO: I am thinking a datastructue in the backend that implements an IExternalEventSource interface wich contains details if is is filebased or API based and what kind of auth. and probably
 * some default settings for races etc.
 *
 * This component should return the neccessary data to create a new event.
 */

const model = defineModel<CreateEventRequest>({ required: true });

const separator = ref<string>('event.willbecreatedfromexternaldatasource');

// Ref to the grid component
const grid = ref<IGridCompExposed<ExternalEventDatasourceResponse> | null>(null);

// Contains any error message we want to show in the view
const errorMessage = ref<ITranslationKey | null>(null);

const { AddLoader, LoadState } = useTaskLoader();

// This is the current externalDatasource. We only have isonen for now
const selectedSource = ref<SelectLineValueType>('ISonen');

// The currently selected item from the external datasource
const selectedIsonenItem = ref<SelectLineValueType | null>(null);

// The currently selected event from the external datasource
const selectedExternalDatasource = computed(() => {
    if (!selectedSource.value) {
        return null;
    }
    return externalDatasources.value.find((x) => x.Name === selectedSource.value);
});

// The loader and state for fetching the isonen items
const { AddLoader: GetIsonenItemsAddLoader, LoadState: GetIsonenItemsLoadState } = useTaskLoader();

// The loader and state for fetching the isonen items
const { AddLoader: FetchIsonenEventAddLoader, LoadState: FetchIsonenEventLoadState } = useTaskLoader();

// The loader and state for handling the isonen event data
const { AddLoader: HandleIsonenEventAddLoader, LoadState: HandleIsonenEventLoadState } = useTaskLoader();

/**
 * A list of the externals
 */
const externalDatasources = ref<ExternalDataSource[]>([]);

/**
 * If the load of the external events failed
 */
const externalEventsLoadFailed = ref<'timeout' | 'unauthorized' | false>(false);

// Add isonen
externalDatasources.value.push(
    ExternalDataSource.FromJsValues({
        Name: 'ISonen',
        Type: 'FileDrop',
        LogoSource: 'https://cdn-assets.productfruits.com/fefd9ef6-0392-4624-9df9-269985775404',
    }),
);

// Contains the list of all the isonen clubs/items we can fetch data from
const isonenItems = ref<ISonenItem[]>([]);

/**
 * Contains the selected item from isonen to use in the dropdown
 */
const isonenDropdownElements = computed(() => {
    const collator = new Intl.Collator('nb');

    return isonenItems.value
        .map((item) => {
            return {
                value: item.APIID,
                text: item.DescriptiveName,
            } as SelectLine;
        })
        .sort((a, b) => collator.compare(a.text, b.text));
});

// Contains a list of all the available events from the selected datasource and club/item
const availableExternalDatasourceEvents = ref<ExternalDatasourceEvent[]>([]);

const isonenEventsDropdownElements = computed(() => {
    if (!availableExternalDatasourceEvents.value) {
        return [];
    }
    return availableExternalDatasourceEvents.value.map((item) => {
        return {
            value: item.Key,
            text: formatEventName(item),
            subtext: formatDateDefault(item.Date, 'DATE'),
        } as SelectLine;
    });
});

const selectedExternalDatasourceEventKey = ref<string | null>(null);

/**
 * Return a formatted name for the event
 * The name contains the name, date and location (if available)
 * @param {ExternalDatasourceEvent} event
 */
const formatEventName = (event: ExternalDatasourceEvent) => {
    const location = event.Location ? `(${event.Location})` : '';

    return `${event.Name} ${location}`.trim();
};

/**
 * Datasource for the external datasources dropdown.
 */
const computedExternalDatasources = computed(() => {
    const options = externalDatasources.value.map((source) => {
        return {
            text: source.Name,
            value: source.Name,
            color: source.LogoSource,
        } as SelectLine;
    });

    return options;
});

/**
 * This will handle the file that is dropped into the dropzone.
 * @param {File[]} files
 */
const onFileChanged = (files: File[]) => {
    if (files.length > 0) {
        uploadFiles(files);
    }
};

const processedFile = ref<boolean>(false);

/**
 * Contains a list of all the files that have been uploaded.
 */
const modelList = ref<ExternalEventDatasourceResponse[]>([]);

/**
 * Contains a list of duplicate contestants that will be ignored
 */
const duplicates = ref<ImportDuplicateContestant[] | null>(null);

/**
 * Select the file we want to use
 * @param response
 */
const selectFile = (response: ExternalEventDatasourceResponse) => {
    duplicates.value = null;

    if (!model.value.EventInfo) {
        model.value.EventInfo = response.EventInfo;
    }

    duplicates.value = response.Duplicates;

    model.value.EventInfo!.Name = response.EventInfo?.Name ?? '';
    model.value.EventInfo!.Date = response.EventInfo?.Date ?? new Date();
    model.value.EventInfo!.EventStart = response.EventInfo?.EventStart ?? new Date();
    model.value.EventInfo!.EventEnd = response.EventInfo?.EventEnd ?? new Date();
    model.value.EventInfo!.Location = response.EventInfo?.Location ?? null;
    model.value.EventInfo!.FederationID = response.EventInfo?.FederationID ?? 0;
    model.value.EventInfo!.SportID = response.EventInfo?.SportID ?? 0;
    model.value.EventInfo!.SportlevelID = response.EventInfo?.SportlevelID ?? null;
    model.value.EventInfo!.DisciplineID = response.EventInfo?.DisciplineID ?? null;

    model.value.SourceEventIncludeContestants = true;

    // We set the organizer to the first in the list if we dont have one already
    if (response.EventInfo?.OrganizerID !== null && response.EventInfo) {
        model.value.EventInfo!.OrganizerID = response.EventInfo?.OrganizerID;
    }

    if (response.EventInfo?.SportID) {
        model.value.EventInfo!.SportID = response.EventInfo?.SportID;
    }

    if (response.EventInfo?.FederationID) {
        model.value.EventInfo!.FederationID = response.EventInfo?.FederationID;
    }

    model.value.Categories = response.Categories;
    model.value.Races = response.Races;
    model.value.Contestants = response.Contestants;
    model.value.CategoryGroupConnection = response.CategoryGroupConnection;
    model.value.GroupToRaceOneToOne = response.GroupToRaceOneToOne;
    model.value.ImportFormat = response.ImportFormat;
    model.value.EventSetup = response.EventSetup;

    processedFile.value = true;
    grid.value?.refreshCachedData();
};

const onCellClicked = (rowid: string, colid: string) => {
    // selectFile(row);
    const row = modelList.value.find((x) => x.EventInfo?.Name === rowid);

    if (!row) {
        return;
    }
    selectFile(row);
};

const uploadFiles = async (files: File[]) => {
    errorMessage.value = null;
    await AddLoader(async () => {
        // TODO: We should probably add a check to make sure we only upload one file at a time
        // Create a FormData object to store the form data
        const formData = new FormData();

        // Append each selected file to the FormData object
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }
        try {
            modelList.value = await ExternalEventDatasourceDao.HandleExternalDatasourceFiles(formData, 'FileDrop');

            selectedSource.value = null;

            if (!modelList.value || modelList.value.length === 0) {
                throw new Error('No data returned from external datasource');
            }

            selectFile(modelList.value[0]);

            useHelper().setFocus();
        } catch (e) {
            if ((e as Error).cause == '409') {
                errorMessage.value = { key: (e as Error).message } as ITranslationKey;
                return;
            }
            console.error(e);
            useToastStore().addError('general.error');
        }
    }, '');
};

/**
 * Set up the datasource
 */
const dataSource = new GridDataSource<ExternalEventDatasourceResponse>({
    totalNumberOfRows: () => modelList.value.length,
    rowCountGetter: () => modelList.value.length,
    rowGetter: (params: IGridRowGetterParams<ExternalEventDatasourceResponse>) => {
        params.successCallback(modelList.value, modelList.value, modelList.value.length);
    },
});

const gridColumnDefinitions = [];
const nameCol = new GridColumnOptionsBuilder<ExternalEventDatasourceResponse>('EventInfo.Name', 'eventinfo.name', 'name')
    .ReadOnly()
    .SortDisabled()
    .CellRenderer(ValidatedText)
    .Build();
nameCol.valueGetter = (row: ExternalEventDatasourceResponse) => {
    return row.EventInfo?.Name || '';
};
gridColumnDefinitions.push(nameCol);

const dateCol = new GridColumnOptionsBuilder<ExternalEventDatasourceResponse>('EventInfo.Date', 'eventinfo.date', 'date')
    .ReadOnly()
    .SortDisabled()
    .CellRenderer(DateComp, { format: 'DATE' })
    .Build();
dateCol.valueGetter = (row: ExternalEventDatasourceResponse) => {
    return row.EventInfo?.Date;
};
gridColumnDefinitions.push(dateCol);

const raceCol = new GridColumnOptionsBuilder<ExternalEventDatasourceResponse>('Races', 'race.races', 'races')
    .ReadOnly()
    .SortDisabled()
    .CellRenderer(ValidatedText)
    .Build();
raceCol.valueGetter = (row: ExternalEventDatasourceResponse) => {
    return row.Races.map((x) => x.Name).join(', ') || '';
};

gridColumnDefinitions.push(raceCol);

const categoryCol = new GridColumnOptionsBuilder<ExternalEventDatasourceResponse>('Races', 'category.categories', 'categories')
    .ReadOnly()
    .SortDisabled()
    .CellRenderer(ValidatedText)
    .Build();
categoryCol.valueGetter = (row: ExternalEventDatasourceResponse) => {
    return row.Categories.map((x) => x.Name).join(', ') || '';
};

gridColumnDefinitions.push(categoryCol);

const contestantCol = new GridColumnOptionsBuilder<ExternalEventDatasourceResponse>('Contestants', 'focus.numcontestants', 'contestants')
    .ReadOnly()
    .SortDisabled()
    .CellRenderer(ValidatedInt)
    .Build();
contestantCol.valueGetter = (row: ExternalEventDatasourceResponse) => {
    return row.Contestants ? row.Contestants.length : 0;
};

gridColumnDefinitions.push(contestantCol);

/**
 * The row class rules for the grid
 */
const rowClassRules: IGridRowCssRule<ExternalEventDatasourceResponse>[] = [
    {
        className: 'grid-row-success',
        func: (data: ExternalEventDatasourceResponse) => model.value?.EventInfo?.Name === data.EventInfo?.Name,
    },
];

const gridSetup = new GridSetup<ExternalEventDatasourceResponse>({
    rowIdGetter: (row: ExternalEventDatasourceResponse) => row.EventInfo?.Name!,
    hasCheckboxSelectionColumn: false,
    rowSelection: 'single',
    hideStatusbar: true,
    datasource: dataSource,
    rowClassRules: rowClassRules,
    GridColumnDefinition: new GridColumnDefinition({
        CommonColumnOptions: {
            sortable: false,
            filterable: false,
            editable: false,
        },
        ColumnOptions: gridColumnDefinitions,
    }),
});

/**
 * Currently selected isonen item
 */
const currentIsonenItem = ref<ISonenItem | null>(null);

/**
 * Fetch all the events/items for the currently selected item.
 */
const fetchEventsForCurrentItem = async () => {
    externalEventsLoadFailed.value = false;

    if (currentIsonenItem.value === null) {
        availableExternalDatasourceEvents.value = [];
        return;
    }

    await FetchIsonenEventAddLoader(async () => {
        try {
            let extEvents = await ExternalEventDatasourceDao.GetDatasourceEvents(currentIsonenItem.value!, '');

            // We should order these by date desc and then move the one with the next date to the top
            extEvents = extEvents.sort((a, b) => b.Date.getTime() - a.Date.getTime());

            let nextEventIndex = -1;
            for (let i = 0; i < extEvents.length; i++) {
                const ev = extEvents[i];
                // As soon as it is older than today we stop
                if (ev.Date < new Date()) {
                    break;
                }
                nextEventIndex = i;
            }

            if (nextEventIndex !== -1) {
                // Remove from the original position and add to the top
                let nextItem = extEvents.splice(nextEventIndex, 1)[0];
                extEvents.unshift(nextItem);
            }

            availableExternalDatasourceEvents.value = extEvents;
        } catch (e) {
            externalEventsLoadFailed.value = 'timeout';
        }
    }, '');
};

/**
 * Will load the details for the currently selected event
 */
const fetchDetailsForCurrentItem = async () => {
    if (!currentIsonenItem.value || !selectedExternalDatasourceEventKey.value) {
        return;
    }

    await HandleIsonenEventAddLoader(async () => {
        try {
            // TODO: We need to load the ExternalEventDatasourceResponse for the given event item
            modelList.value = await ExternalEventDatasourceDao.GetEventDatasourceResponse(
                currentIsonenItem.value!,
                selectedExternalDatasourceEventKey.value,
            );
            if (modelList.value.length === 0) {
                return;
            }
            processedFile.value = true;
            selectFile(modelList.value[0]);
        } catch (e) {
            // We need to show a error message saying we could not load the event from isonen
            if ((e as Error).cause == '401') {
                externalEventsLoadFailed.value = 'unauthorized';
                return;
            }
            externalEventsLoadFailed.value = 'timeout';
        }
    }, '');
};

/**
 * We watch for changes on the selected event key and then load the data for that event
 */
watch(selectedExternalDatasourceEventKey, async () => await fetchDetailsForCurrentItem());

/**
 * This listens for changes for the selected isonen item and get a list of the events for that item
 */
watch(selectedIsonenItem, async (nv) => {
    currentIsonenItem.value = isonenItems.value?.find((x) => x.APIID === (nv as number)) ?? null;
    fetchEventsForCurrentItem();
});

onMounted(async () => {
    await GetIsonenItemsAddLoader(async () => {
        isonenItems.value = await ExternalEventDatasourceDao.IsonenItemsForUser();

        // We preselect the first item
        if (isonenItems.value && isonenItems.value.length > 0) {
            selectedIsonenItem.value = isonenItems.value[0].APIID;
        }
    }, '');
});
</script>
